import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileText, Plus, Search, X } from 'lucide-react';
import { getCookie } from '../utils/utils';
import FilterDropdown from './FilterDropdown';
import { useCaps } from '../contexts/CapabilitiesContext';

export interface GuaranteedOutput {
  filename: string;
  name: string;
  uploaded_at: string;
  service?: string;
  status?: string;
}

interface ServiceOption {
  key: string;
  label: string;
}

interface GuaranteedOutputsProps {
  projectId: number;
  isCatalyxUser?: boolean;
}

type DateFilterValue = '' | '7d' | '30d' | '90d';

const DATE_RANGE_DEFS: { value: DateFilterValue; label: string; ms: number }[] = [
  { value: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
  { value: '90d', label: 'Last 90 days', ms: 90 * 24 * 60 * 60 * 1000 },
];

const GuaranteedOutputs: React.FC<GuaranteedOutputsProps> = ({ projectId, isCatalyxUser }) => {
  const { can } = useCaps();
  const canUploadGos = can('gos_upload');
  const [outputs, setOutputs] = useState<GuaranteedOutput[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilename, setSelectedFilename] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [fileServices, setFileServices] = useState<string[]>([]);
  const [singleService, setSingleService] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ filename: string; name: string } | null>(null);
  const [deletingFilename, setDeletingFilename] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadOutputs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/reports/`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.reports)) {
        setOutputs(data.reports);
        if (Array.isArray(data.services)) {
          setServices(data.services);
        }
      } else {
        setOutputs([]);
      }
    } catch {
      setOutputs([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void loadOutputs();
  }, [loadOutputs]);

  const serviceLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    services.forEach((service) => map.set(service.key, service.label));
    return map;
  }, [services]);

  const statusOptions = useMemo(() => {
    const statuses = new Set(outputs.map((item) => (item.status || 'done').toLowerCase()));
    return Array.from(statuses).map((status) => ({
      value: status,
      label: status.charAt(0).toUpperCase() + status.slice(1),
    }));
  }, [outputs]);

  const serviceOptions = useMemo(() => {
    const keys = new Set(
      outputs.map((item) => item.service).filter((service): service is string => Boolean(service))
    );
    const catalogOrder = services.map((service) => service.key);
    return Array.from(keys)
      .sort((a, b) => {
        const ai = catalogOrder.indexOf(a);
        const bi = catalogOrder.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      })
      .map((key) => ({
        value: key,
        label: serviceLabelByKey.get(key) || key,
      }));
  }, [outputs, services, serviceLabelByKey]);

  const dateOptions = useMemo(() => {
    const now = Date.now();
    return DATE_RANGE_DEFS.filter((range) =>
      outputs.some((item) => {
        const uploadedAt = new Date(item.uploaded_at).getTime();
        return !Number.isNaN(uploadedAt) && uploadedAt >= now - range.ms;
      })
    ).map((range) => ({ value: range.value, label: range.label }));
  }, [outputs]);

  const filteredOutputs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const now = Date.now();
    const dateCutoff = dateFilter
      ? DATE_RANGE_DEFS.find((range) => range.value === dateFilter)?.ms
      : null;

    return outputs.filter((item) => {
      const status = (item.status || 'done').toLowerCase();
      if (statusFilter && status !== statusFilter) return false;
      if (serviceFilter && item.service !== serviceFilter) return false;
      if (dateCutoff != null) {
        const uploadedAt = new Date(item.uploaded_at).getTime();
        if (Number.isNaN(uploadedAt) || uploadedAt < now - dateCutoff) return false;
      }
      if (query) {
        const label = item.name.toLowerCase();
        const serviceLabel = (serviceLabelByKey.get(item.service || '') || '').toLowerCase();
        if (!label.includes(query) && !serviceLabel.includes(query)) return false;
      }
      return true;
    });
  }, [outputs, statusFilter, serviceFilter, dateFilter, searchQuery, serviceLabelByKey]);

  const selectedOutput = filteredOutputs.find((item) => item.filename === selectedFilename)
    ?? outputs.find((item) => item.filename === selectedFilename)
    ?? null;

  const downloadUrl = (filename: string) =>
    `/api/projects/${projectId}/reports/${encodeURIComponent(filename)}/download/`;

  const previewUrl = (filename: string) =>
    `/api/projects/${projectId}/reports/${encodeURIComponent(filename)}/preview/`;

  const handlePreview = (filename: string) => {
    setSelectedFilename(filename);
    setPreviewLoading(true);
  };

  const handleDeleteClick = (filename: string, name: string) => {
    setDeleteError(null);
    setDeleteConfirm({ filename, name });
  };

  const handleCloseDeleteModal = () => {
    if (deletingFilename) return;
    setDeleteConfirm(null);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    const { filename } = deleteConfirm;
    setDeletingFilename(filename);
    setDeleteError(null);

    const csrfToken = getCookie('csrftoken');
    try {
      const res = await fetch(
        `/api/projects/${projectId}/reports/${encodeURIComponent(filename)}/delete/`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'X-CSRFToken': csrfToken || '' },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDeleteError(data.error || 'Failed to delete report.');
        return;
      }

      if (selectedFilename === filename) {
        setSelectedFilename(null);
        setPreviewLoading(false);
      }

      setDeleteConfirm(null);
      await loadOutputs();
    } catch {
      setDeleteError('Failed to delete report.');
    } finally {
      setDeletingFilename(null);
    }
  };

  const handleOpenUploadForm = () => {
    setShowUploadForm(true);
    setUploadName('');
    setFiles([]);
    setFileNames([]);
    setFileServices([]);
    setSingleService('');
    setUploadError(null);
  };

  const handleCloseUploadForm = () => {
    if (!uploading) {
      setShowUploadForm(false);
      setUploadName('');
      setFiles([]);
      setFileNames([]);
      setFileServices([]);
      setSingleService('');
      setUploadError(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(e.target.files || []);
    setFiles(chosen);
    setUploadError(null);
    setFileNames(chosen.map((file) => file.name));
    setFileServices(chosen.map(() => singleService || ''));
    if (chosen.length === 1 && !uploadName) {
      setUploadName(chosen[0].name);
    }
  };

  const handleSubmitUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) {
      setUploadError('Please select at least one file.');
      return;
    }

    const servicesForFiles =
      files.length === 1
        ? [singleService]
        : fileServices.length === files.length
          ? fileServices
          : files.map(() => '');

    if (servicesForFiles.some((service) => !service)) {
      setUploadError('Please select a service type for each file.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    const csrfToken = getCookie('csrftoken');
    const failures: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        const nameFromList =
          fileNames.length === files.length && fileNames[i] != null
            ? fileNames[i].trim() || file.name
            : file.name;
        const effectiveName = files.length === 1 ? uploadName.trim() || file.name : nameFromList;
        formData.append('file', file);
        formData.append('name', effectiveName);
        formData.append('service', servicesForFiles[i]);

        const res = await fetch(`/api/projects/${projectId}/reports/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-CSRFToken': csrfToken || '' },
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          failures.push(`${file.name}: ${data.error || res.statusText || 'Upload failed'}`);
        }
      }

      if (failures.length > 0) {
        setUploadError(`Failed to upload: ${failures.join('; ')}`);
        await loadOutputs();
        return;
      }

      handleCloseUploadForm();
      await loadOutputs();
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Title bar: full-bleed, aligned with header */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <h1 className="text-xl font-bold text-gray-900">View Guaranteed Outputs</h1>
      </div>

      <div className="flex flex-1 min-h-0 flex-col gap-4 px-4 pb-4 pt-4">
      {/* Filters + search + Upload */}
      <div className="shrink-0 border border-gray-200 bg-white px-6 py-4">
        <div className="flex flex-wrap items-center gap-3">
          {statusOptions.length > 0 && (
            <FilterDropdown
              placeholder="Status"
              value={statusFilter}
              options={statusOptions}
              onChange={setStatusFilter}
            />
          )}

          {serviceOptions.length > 0 && (
            <FilterDropdown
              placeholder="Service"
              value={serviceFilter}
              options={serviceOptions}
              onChange={setServiceFilter}
            />
          )}

          {dateOptions.length > 0 && (
            <FilterDropdown
              placeholder="Date"
              value={dateFilter}
              options={dateOptions}
              onChange={(value) => setDateFilter(value as DateFilterValue)}
            />
          )}

          <div className="relative min-w-[200px] flex-1 max-w-md">
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              className="h-10 w-full border border-gray-300 bg-white pl-3 pr-10 text-sm text-gray-700"
            />
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>

          {canUploadGos && (
            <button
              type="button"
              onClick={handleOpenUploadForm}
              className="ml-auto inline-flex items-center gap-2 rounded-lg bg-[#655FA8] px-4 py-2 text-sm font-medium text-white hover:bg-[#564f96]"
            >
              <Plus className="w-4 h-4" />
              Upload GOs
            </button>
          )}
        </div>
      </div>

      {/* List and preview */}
      <div className="flex flex-1 min-h-0 gap-4">
        <div className="w-[34%] min-w-[280px] max-w-[420px] overflow-y-auto border border-gray-200 bg-white">
          {loading ? (
            <p className="p-6 text-sm text-gray-500">Loading guaranteed outputs...</p>
          ) : filteredOutputs.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">No guaranteed outputs match your filters.</p>
          ) : (
            <ul>
              {filteredOutputs.map((item) => {
                const isSelected = selectedFilename === item.filename;
                return (
                  <li key={item.filename} className="border-b border-gray-100 last:border-b-0">
                    <div className={`px-5 py-4 ${isSelected ? 'bg-[#F4F3FA]' : ''}`}>
                      <p className="text-sm font-bold text-gray-900">{item.name}</p>
                      <div className="mt-3 flex items-center gap-4 text-sm">
                        <button
                          type="button"
                          onClick={() => handlePreview(item.filename)}
                          className="text-[#2A9CC4] underline hover:text-[#1a7a9a]"
                        >
                          Preview
                        </button>
                        <a
                          href={downloadUrl(item.filename)}
                          download
                          className="inline-flex items-center gap-1.5 text-[#2A9CC4] underline hover:text-[#1a7a9a]"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                        {isCatalyxUser && (
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(item.filename, item.name)}
                            disabled={deletingFilename === item.filename}
                            className="text-red-600 underline hover:text-red-700 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden border border-gray-200 bg-white">
          {selectedOutput ? (
            <div className="flex-1 min-h-0 flex flex-col relative">
              {previewLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 text-sm text-gray-500">
                  Loading preview...
                </div>
              )}
              <iframe
                key={selectedOutput.filename}
                title={selectedOutput.name}
                src={previewUrl(selectedOutput.filename)}
                className="h-full w-full border-0 bg-white"
                onLoad={() => setPreviewLoading(false)}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-lg">
              Content would appear here
            </div>
          )}
        </div>
      </div>
      </div>

      {deleteConfirm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-go-title"
          onClick={handleCloseDeleteModal}
        >
          <div
            className="w-full max-w-md border border-gray-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 id="delete-go-title" className="text-lg font-semibold text-gray-900">
                Delete guaranteed output?
              </h2>
              <button
                type="button"
                onClick={handleCloseDeleteModal}
                disabled={!!deletingFilename}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete{' '}
                <span className="font-medium text-gray-900">{deleteConfirm.name}</span>? This cannot
                be undone.
              </p>
              {deleteError && <p className="mt-3 text-sm text-red-600">{deleteError}</p>}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseDeleteModal}
                disabled={!!deletingFilename}
                className="border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteConfirm()}
                disabled={!!deletingFilename}
                className="bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deletingFilename === deleteConfirm.filename ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadForm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Upload GOs</h2>
              <button type="button" onClick={handleCloseUploadForm} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitUpload} className="space-y-5 px-6 py-5">
              <div>
                <label htmlFor="go-files" className="mb-1 block text-sm font-medium text-gray-700">
                  File{files.length !== 1 ? 's' : ''}
                </label>
                <input
                  id="go-files"
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.ppt,.pptx"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-600"
                />
              </div>

              {files.length === 1 && (
                <>
                  <div>
                    <label htmlFor="go-name" className="mb-1 block text-sm font-medium text-gray-700">
                      Display name
                    </label>
                    <input
                      id="go-name"
                      type="text"
                      value={uploadName}
                      onChange={(e) => setUploadName(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="go-service" className="mb-1 block text-sm font-medium text-gray-700">
                      Service
                    </label>
                    <select
                      id="go-service"
                      value={singleService}
                      onChange={(e) => setSingleService(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      required
                    >
                      <option value="" disabled hidden>
                        Select service
                      </option>
                      {services.map((service) => (
                        <option key={service.key} value={service.key}>
                          {service.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {files.length > 1 && (
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {files.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="rounded-lg border border-gray-200 p-3 space-y-3">
                      <p className="text-xs font-medium text-gray-500 truncate">{file.name}</p>
                      <input
                        type="text"
                        value={fileNames[index] ?? file.name}
                        onChange={(e) => {
                          const next = [...fileNames];
                          next[index] = e.target.value;
                          setFileNames(next);
                        }}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Display name"
                      />
                      <select
                        value={fileServices[index] ?? ''}
                        onChange={(e) => {
                          const next = [...fileServices];
                          next[index] = e.target.value;
                          setFileServices(next);
                        }}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        required
                      >
                        <option value="" disabled hidden>
                        Select service
                      </option>
                        {services.map((service) => (
                          <option key={service.key} value={service.key}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseUploadForm}
                  disabled={uploading}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || files.length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#655FA8] px-4 py-2 text-sm font-medium text-white hover:bg-[#564f96] disabled:opacity-60"
                >
                  <FileText className="h-4 w-4" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuaranteedOutputs;
